import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Edit,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Linkedin,
  Twitter,
  Facebook
} from 'lucide-react';
import { updateContactInfo } from '../../utils/contentManager';

const ContactManagement = () => {
  const [contactData, setContactData] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({});
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const contactFields = [
    { key: 'email', label: 'Email Address', type: 'email', icon: Mail, placeholder: 'contact@ohasystem.com' },
    { key: 'phone', label: 'Phone Number', type: 'tel', icon: Phone, placeholder: '+1 (555) 123-4567' },
    { key: 'address', label: 'Address', type: 'text', icon: MapPin, placeholder: 'San Francisco, CA' },
    { key: 'businessHours', label: 'Business Hours', type: 'text', icon: Clock, placeholder: 'Mon-Fri: 9AM-6PM PST' }
  ];

  const socialFields = [
    { key: 'linkedin', label: 'LinkedIn URL', type: 'url', icon: Linkedin, placeholder: 'https://linkedin.com/company/ohasystem' },
    { key: 'twitter', label: 'Twitter URL', type: 'url', icon: Twitter, placeholder: 'https://twitter.com/ohasystem' },
    { key: 'facebook', label: 'Facebook URL', type: 'url', icon: Facebook, placeholder: 'https://facebook.com/ohasystem' }
  ];

  useEffect(() => {
    loadContactData();
  }, []);

  const loadContactData = () => {
    try {
      const data = JSON.parse(localStorage.getItem('oha_contact_data') || '{}');
      setContactData(data);
      setFormData({ ...data, ...data.socialMedia });
    } catch (error) {
      console.error('Error loading contact data:', error);
      setContactData({});
      setFormData({});
    }
  };

  const handleEdit = (field) => {
    setEditingField(field);
    setError('');
    setSuccess('');
  };

  const handleSave = (field) => {
    try {
      let updatedData = { ...contactData };

      if (socialFields.some(f => f.key === field.key)) {
        updatedData.socialMedia = {
          ...updatedData.socialMedia,
          [field.key]: formData[field.key]
        };
      } else {
        updatedData[field.key] = formData[field.key];
      }

      const result = updateContactInfo(updatedData);

      if (result) {
        setContactData(updatedData);
        setEditingField(null);
        setSuccess(`${field.label} updated successfully`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to update contact information');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to update contact information');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleCancel = () => {
    setFormData({ ...contactData, ...contactData.socialMedia });
    setEditingField(null);
  };

  const handleInputChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const getFieldValue = (field) => {
    if (socialFields.some(f => f.key === field.key)) {
      return contactData.socialMedia?.[field.key] || '';
    }
    return contactData[field.key] || '';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Contact Management</h1>
        <p className="text-gray-600 mt-2">Manage company contact information and social media links</p>
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">{error}</span>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3"
          >
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">{success}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contact Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Contact Information</h3>
          <div className="space-y-6">
            {contactFields.map((field) => (
              <div key={field.key} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <field.icon className="w-5 h-5 text-gray-500 mr-2" />
                    <label className="text-sm font-medium text-gray-700">{field.label}</label>
                  </div>
                  {editingField?.key === field.key ? (
                    <div className="flex items-center space-x-2">
                      <button onClick={() => handleSave(field)} className="text-green-600 hover:text-green-700 transition-colors duration-200">
                        <Save className="w-4 h-4" />
                      </button>
                      <button onClick={handleCancel} className="text-gray-600 hover:text-gray-700 transition-colors duration-200">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => handleEdit(field)} className="text-blue-600 hover:text-blue-700 transition-colors duration-200">
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {editingField?.key === field.key ? (
                  <input
                    type={field.type}
                    value={formData[field.key] || ''}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    placeholder={field.placeholder}
                  />
                ) : (
                  <div className="bg-gray-50 rounded-lg p-3 min-h-[40px] flex items-center">
                    <p className="text-gray-900">{getFieldValue(field) || <span className="text-gray-500 italic">Not set</span>}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Social Media Links</h3>
          <div className="space-y-6">
            {socialFields.map((field) => (
              <div key={field.key} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <field.icon className="w-5 h-5 text-gray-500 mr-2" />
                    <label className="text-sm font-medium text-gray-700">{field.label}</label>
                  </div>
                  {editingField?.key === field.key ? (
                    <div className="flex items-center space-x-2">
                      <button onClick={() => handleSave(field)} className="text-green-600 hover:text-green-700 transition-colors duration-200">
                        <Save className="w-4 h-4" />
                      </button>
                      <button onClick={handleCancel} className="text-gray-600 hover:text-gray-700 transition-colors duration-200">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => handleEdit(field)} className="text-blue-600 hover:text-blue-700 transition-colors duration-200">
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {editingField?.key === field.key ? (
                  <input
                    type={field.type}
                    value={formData[field.key] || ''}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    placeholder={field.placeholder}
                  />
                ) : (
                  <div className="bg-gray-50 rounded-lg p-3 min-h-[40px] flex items-center">
                    {getFieldValue(field) ? (
                      <a
                        href={getFieldValue(field)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 underline"
                      >
                        {getFieldValue(field)}
                      </a>
                    ) : (
                      <span className="text-gray-500 italic">Not set</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Preview */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-blue-800 mb-4">Contact Information Preview</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="font-medium text-blue-700 mb-3">Current Contact Details</h5>
            <div className="space-y-2 text-sm text-blue-600">
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                <span>{contactData.email || 'Not set'}</span>
              </div>
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-2" />
                <span>{contactData.phone || 'Not set'}</span>
              </div>
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-2" />
                <span>{contactData.address || 'Not set'}</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                <span>{contactData.businessHours || 'Not set'}</span>
              </div>
            </div>
          </div>
          <div>
            <h5 className="font-medium text-blue-700 mb-3">Social Media Links</h5>
            <div className="space-y-2 text-sm text-blue-600">
              <div className="flex items-center">
                <Linkedin className="w-4 h-4 mr-2" />
                <span>{contactData.socialMedia?.linkedin || 'Not set'}</span>
              </div>
              <div className="flex items-center">
                <Twitter className="w-4 h-4 mr-2" />
                <span>{contactData.socialMedia?.twitter || 'Not set'}</span>
              </div>
              <div className="flex items-center">
                <Facebook className="w-4 h-4 mr-2" />
                <span>{contactData.socialMedia?.facebook || 'Not set'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactManagement;
